import React, { useState, useEffect, useMemo } from 'react';
import {
  Paper,
  Text,
  NumberInput,
  TextInput,
  Button,
  Group,
  Stack,
  Alert,
  Divider,
  Box,
  Checkbox,
  ScrollArea,
  Select
} from '@mantine/core';
import {
  IconAlertCircle,
  IconRun,
  IconInfoCircle,
  IconCommand,
  IconMapPin,
  IconSchool,
  IconCoin,
  IconUserOff,
  IconSkull,
  IconBellRinging,
  IconUsersGroup,
  IconDeviceFloppy,
  IconPower,
  IconPaw,
  IconRefresh,
  IconChartBar,
  IconArrowUp
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { Action, executeAction } from '../api/actions';
import { Character } from '../api/characters';

interface ActionPropertiesProps {
  selectedAction: Action | null;
  selectedCharacter: Character | null;
  serverConnected: boolean;
}

/**
 * Action Properties component
 * Displays action properties and allows executing actions on characters
 */
const ActionProperties: React.FC<ActionPropertiesProps> = ({
  selectedAction,
  selectedCharacter,
  serverConnected
}) => {
  // State for form values
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  // State for form submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if buttons should be disabled
  const isButtonDisabled = !serverConnected || !selectedAction || !selectedCharacter || isSubmitting;

  // Reset form when selected action changes
  useEffect(() => {
    if (selectedAction) {
      // Initialize form with default values
      const initialValues: Record<string, any> = {};
      selectedAction.parameters.forEach(param => {
        if (param.default !== undefined) {
          initialValues[param.name] = param.default;
        } else if (param.type === 'number') {
          initialValues[param.name] = param.min || 0;
        } else if (param.type === 'boolean') {
          initialValues[param.name] = false;
        } else {
          initialValues[param.name] = '';
        }
      });
      setFormValues(initialValues);
    }
  }, [selectedAction]);

  // Update form value
  const updateFormValue = (name: string, value: any) => {
    setFormValues(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Validate form
  const validateForm = (): boolean => {
    if (!selectedAction) return false;

    for (const param of selectedAction.parameters) {
      if (param.required) {
        const value = formValues[param.name];
        if (value === undefined || value === null || value === '') {
          setError(`${param.name} is required`);
          return false;
        }
      }

      if (param.type === 'number') {
        const value = formValues[param.name];
        if (value !== undefined && value !== null) {
          if (param.min !== undefined && value < param.min) {
            setError(`${param.name} must be at least ${param.min}`);
            return false;
          }
          if (param.max !== undefined && value > param.max) {
            setError(`${param.name} must be at most ${param.max}`);
            return false;
          }
        }
      }
    }

    return true;
  };

  // Handle action execution
  const handleExecuteAction = async () => {
    if (isButtonDisabled || !selectedAction || !selectedCharacter) return;

    // Validate form
    if (!validateForm()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Process parameter values to ensure correct types
      const processedParams = { ...formValues };
      selectedAction.parameters.forEach(param => {
        if (param.type === 'number' && processedParams[param.name] !== undefined) {
          // Ensure number parameters are properly formatted as numbers
          processedParams[param.name] = Number(processedParams[param.name]);
        }
      });
      const result = await executeAction({
        actionId: selectedAction.id,
        parameters: processedParams,
        characterId: selectedCharacter.id,
        characterName: selectedCharacter.name
      });

      notifications.show({
        title: 'Action Executed',
        message: `${selectedAction.name} executed successfully on ${selectedCharacter.name}`,
        color: 'green'
      });
    } catch (err) {
      console.error('Failed to execute action:', err);
      setError('Failed to execute action. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Action icon mapping
  const getActionIcon = useMemo(() => {
    return (actionId: string) => {
      switch (actionId) {
        case 'warp':
          return <IconMapPin size={20} />;
        case 'learn_all_skill':
          return <IconSchool size={20} />;
        case 'insert_gold':
          return <IconCoin size={20} />;
        case 'kick':
          return <IconUserOff size={20} />;
        case 'kill_target':
          return <IconSkull size={20} />;
        case 'notice':
          return <IconBellRinging size={20} />;
        case 'recall_player':
          return <IconUsersGroup size={20} />;
        case 'saveall':
          return <IconDeviceFloppy size={20} />;
        case 'shutdown':
          return <IconPower size={20} />;
        case 'learn_creature_all_skill':
          return <IconPaw size={20} />;
        case 'reset_summon_skill':
          return <IconRefresh size={20} />;
        case 'stat':
          return <IconChartBar size={20} />;
        case 'set_player_level':
          return <IconArrowUp size={20} />;
        case 'set_creature_level':
          return <IconArrowUp size={20} />;
        default:
          return <IconCommand size={20} />;
      }
    };
  }, []);

  const predefinedLocations = [
    { label: 'Горизон', value: 'horizon', x: 152754, y: 77245 },
    { label: 'Лакси', value: 'laksi', x: 6612, y: 6873 },
    { label: 'Катан', value: 'katan', x: 116840, y: 58309 },
    { label: 'Рондо', value: 'rondo', x: 137722, y: 105483 },
    { label: 'Мертвый город', value: 'dead_city', x: 152579, y: 150916 },
    { label: 'Скрытка', value: 'hidden_village', x: 222229, y: 20189 },
    { label: 'СЛС1', value: 'sls1', x: 132914, y: 87027 },
    { label: 'СЛС2', value: 'sls2', x: 130909, y: 79530 },
    { label: 'ЗШ1', value: 'zsh1', x: 152194, y: 102858 },
    { label: 'ЗШ2', value: 'zsh2', x: 155863, y: 103714 },
    { label: 'КД1', value: 'kd1', x: 99672, y: 103206 },
    { label: 'КД2', value: 'kd2', x: 103234, y: 100317 },
    { label: 'ПП1', value: 'pp1', x: 132691, y: 128033 },
    { label: 'ПП2', value: 'pp2', x: 137456, y: 128113 },
    { label: 'ЭК', value: 'ek', x: 146227, y: 135603 },
    { label: 'ХД', value: 'hd', x: 91926, y: 117134 },
    { label: 'ХПД', value: 'hpd', x: 85798, y: 118104 },
    { label: 'ХИ', value: 'hi', x: 92041, y: 124432 },
    { label: 'ТО Центр', value: 'to_center', x: 94366, y: 126142 },
  ];

  // Render form fields based on action parameters
  const renderFormFields = () => {
    if (!selectedAction) return null;

    // Special UI for warp action: show predefined locations dropdown
    if (selectedAction.id === 'warp') {
      return (
        <>
          <Box mb="md">
            <Text size="sm" mb={5} fw={500}>Predefined Location</Text>
            <Select
              placeholder="Select location..."
              data={predefinedLocations.map(loc => ({ label: loc.label, value: loc.value }))}
              value={null}
              onChange={locValue => {
                const loc = predefinedLocations.find(l => l.value === locValue);
                if (loc) {
                  updateFormValue('x', loc.x);
                  updateFormValue('y', loc.y);
                }
              }}
              clearable
              searchable
              nothingFoundMessage="No locations"
              size="sm"
            />
            <Text size="xs" c="dimmed" mt={5}>
              Choose a location to auto-fill coordinates, or enter them manually below.
            </Text>
          </Box>
          {/* Render x and y fields as usual */}
          {selectedAction.parameters.map(param => {
            if (param.name === 'x' || param.name === 'y') {
              return (
                <Box key={param.name} mb="md">
                  <Text size="sm" mb={5} fw={500}>
                    {param.name} {param.required && <Text span color="red" component="span">*</Text>}
                  </Text>
                  <NumberInput
                    value={formValues[param.name] || 0}
                    onChange={value => updateFormValue(param.name, value)}
                    min={param.min}
                    max={param.max}
                    stepHoldDelay={500}
                    stepHoldInterval={100}
                    hideControls={false}
                    required={param.required}
                    size="sm"
                    placeholder={param.description}
                  />
                  <Text size="xs" c="dimmed" mt={5}>
                    {param.description}
                  </Text>
                </Box>
              );
            }
            return null;
          })}
        </>
      );
    }

    // Default rendering for other actions
    return selectedAction.parameters.map(param => {
      if (param.type === 'number') {
        return (
          <Box key={param.name} mb="md">
            <Text size="sm" mb={5} fw={500}>
              {param.name} {param.required && <Text span color="red" component="span">*</Text>}
            </Text>
            <NumberInput
              value={formValues[param.name] || 0}
              onChange={(value) => updateFormValue(param.name, value)}
              min={param.min}
              max={param.max}
              stepHoldDelay={500}
              stepHoldInterval={100}
              hideControls={false}
              required={param.required}
              size="sm"
              placeholder={param.description}
            />
            <Text size="xs" c="dimmed" mt={5}>
              {param.description}
            </Text>
          </Box>
        );
      } else if (param.type === 'boolean') {
        return (
          <Box key={param.name} mb="md">
            <Checkbox
              label={param.name}
              checked={formValues[param.name] || false}
              onChange={(e) => updateFormValue(param.name, e.currentTarget.checked)}
              description={param.description}
            />
          </Box>
        );
      } else {
        return (
          <Box key={param.name} mb="md">
            <Text size="sm" mb={5} fw={500}>
              {param.name} {param.required && <Text span color="red" component="span">*</Text>}
            </Text>
            <TextInput
              value={formValues[param.name] || ''}
              onChange={(e) => updateFormValue(param.name, e.currentTarget.value)}
              required={param.required}
              size="sm"
              placeholder={param.description}
            />
            <Text size="xs" c="dimmed" mt={5}>
              {param.description}
            </Text>
          </Box>
        );
      }
    });
  };

  return (
    <Paper shadow="xs" p="md" radius={0} h="100%" withBorder className="h-full flex flex-col">
      <Text fw={700} mb="md">Action Properties</Text>

      {selectedAction ? (
        <div className="flex flex-col h-full overflow-auto">
          {/* Action details */}
          <Group className="bg-gray-50 p-3 rounded-md">
            {/* Action icon */}
            <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-gray-100 rounded-full"
                 style={{ marginRight: '10px' }}>
              {selectedAction && getActionIcon(selectedAction.id)}
            </div>

            {/* Action name and description */}
            <div className="flex-grow">
              <div className="flex items-center gap-2">
                <Text fw={700} size="md">
                  {selectedAction.name}
                </Text>
              </div>

              <Text size="xs" mt={4}>
                {selectedAction.description}
              </Text>
            </div>
          </Group>

          <Divider my="sm" />

          {/* Form container - make scrollable */}
          <ScrollArea className="flex-grow mb-4">
            <div className="pr-2">
              {renderFormFields()}

              {/* Selected character info */}
              {selectedCharacter && (
                <Group
                  className="bg-blue-50 p-2 rounded-md mb-4"
                  style={{ marginTop: '10px', marginBottom: '10px' }}
                  wrap="nowrap"
                  align="center"
                >
                  <Box mr={10}>
                    <IconInfoCircle size={20} className="text-blue-500" />
                  </Box>
                  <div>
                    <Text size="xs">Selected Character</Text>
                    <Text size="sm" fw={500}>
                      {selectedCharacter.name} {selectedCharacter.level && `(Level ${selectedCharacter.level})`}
                    </Text>
                  </div>
                </Group>
              )}

              {/* Error message */}
              {error && (
                <Alert 
                  icon={<IconAlertCircle size={20} />} 
                  color="red" 
                  title="Error" 
                  mb="md"
                  withCloseButton
                  onClose={() => setError(null)}
                >
                  {error}
                </Alert>
              )}
            </div>
          </ScrollArea>

          {/* Status indicators */}
          {!selectedCharacter && (
            <Alert
              icon={<IconInfoCircle size={20} />}
              color="blue"
              variant="light"
              p="xs"
              mb="md"
            >
              Select a character to execute actions
            </Alert>
          )}

          {!serverConnected && (
            <Alert
              icon={<IconAlertCircle size={20} />}
              color="red"
              variant="light"
              p="xs"
              mb="md"
            >
              Server is offline
            </Alert>
          )}

          {/* Button container */}
          <Stack className="flex-shrink-0 mt-auto">
            <Button
              leftSection={<IconRun size={20} />}
              color="blue"
              onClick={handleExecuteAction}
              loading={isSubmitting}
              disabled={isButtonDisabled}
              fullWidth
              className={!isButtonDisabled ? "shadow-sm hover:shadow-md transition-shadow" : ""}
            >
              Execute Action
            </Button>
          </Stack>
        </div>
      ) : (
        <div className="h-full flex items-center justify-center">
          <Box className="text-center">
            <IconInfoCircle size={20} className="mx-auto mb-2 text-gray-400" />
            <Text c="dimmed">Select an action from the list</Text>
          </Box>
        </div>
      )}
    </Paper>
  );
};

export default ActionProperties; 